import { Stack, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  useShiftAddEditStore,
  useShiftsAssignment,
  useShiftSeriesAddEditStore,
  useCalendarShiftsStore,
  useShiftRotationAddEditStore,
  usePublishDrawerStore,
  useShareDrawerStore,
  useHardDeleteModalStore,
  useCalendarFilters,
  useAssignShiftDrawerStore,
  useSeriesDrawerState,
  useRotationDrawerState,
  useShiftSeriesEditStore,
  useRotationSeriesEditStore,
} from '@libs/store/shifts';
import { useLocationStore } from '@libs/store/locations';
import { SkeletonProgress } from '@molecules/feedback';
import { useOrgQualificationsStore } from '@libs/store/settings';
import {
  EditRotationDrawer,
  PublishShiftsDrawer,
  EditShiftDrawer,
  EditSeriesDrawer,
  ShareShifts,
  AssignShifts,
} from '../../drawers';
import {
  CalendarUnassignDialog,
  CancelDialog,
  ConfirmAssignOfEntireRecurrenceDialog,
  ConfirmCreateDoubleShiftDialog,
  ShiftHardDeleteDialog,
} from '../../dialogs';
import { useNotification } from '@libs/snackbar';
import { CalendarFilterBar } from '../filtersBar';
import dayjs, { Dayjs } from 'dayjs';
import { useRefreshSyncStore } from '@libs/store/refreshSync';
import { Shift } from '@libs/models/shifts/shift';
import { Calendar } from './calendar';
import { TIMEZONE_FALLBACK } from '@libs/config';
import { DateRangeFilter } from '@libs/store/filters';
import { EDIT_ALLOWED_STATUSES } from '@pages/schedule/constants';
import { parseError } from '@libs/api/errors';
import { Analytics } from '@libs/analytics';
import { RotationDetailsDrawer, SeriesDetailsDrawer } from '@pages/schedule/drawers/shiftDetails';
import { TalentViewDrawer } from '@pages/talents/listPage/drawers/viewTalent';
import { useViewProviderInfoStore } from '@libs/store/talents/talentViewStore';
import { StaffListItem } from '@libs/models/talents/talent';

const WeekDayStartMap: Record<string, number> = {
  Saturday: -1,
  Sunday: 0,
  Monday: 1,
};

export function CalendarWeeklyViewPage() {
  const { showErrorIntl, showError, showSuccessIntl } = useNotification();
  const { refreshTrigger, callRefresh } = useRefreshSyncStore();
  const { activeFilters, setFilter, hasFilter, getFilter, setSortingModel, sortingModel } = useCalendarFilters();
  const { currentLocationID, current: currentLocation, loading: locationsLoading } = useLocationStore();
  const { openShiftSeriesDrawer } = useShiftSeriesAddEditStore();
  const { openShiftRotationDrawer } = useShiftRotationAddEditStore();
  const { openShiftDrawer } = useShiftAddEditStore();
  const { openShiftShareDrawer } = useShareDrawerStore();
  const { openShiftPublishDrawer } = usePublishDrawerStore();
  const [openSeriesDrawer] = useSeriesDrawerState();
  const [openRotationDrawer] = useRotationDrawerState();
  const { openDrawer: openAssignShiftDrawer } = useAssignShiftDrawerStore();
  const {
    assignShift,
    requestAssignShiftOnExternal,
    loadingById,
    cancelShift,
    deleteShift,
    openShiftUnassignDialog,
    checkAssignOverlap,
  } = useShiftsAssignment();
  const { openModal: openHardDeletePopup } = useHardDeleteModalStore();
  const { openDrawer: openViewProviderInfoDrawer } = useViewProviderInfoStore();
  const [dateRange, setDateRange] = useState<{ from: Dayjs; to: Dayjs; weekNumber: number }>({
    from: dayjs().startOf('week'),
    to: dayjs().startOf('week').add(6, 'days'),
    weekNumber: dayjs().week(),
  });
  const [confirmAssignOfEntireRecurrenceDialog, setConfirmAssignOfEntireRecurrenceDialogOpen] = useState(false);
  const [confirmAssignDetails, setConfirmAssignDetails] = useState<{
    providerId: string | null;
    shiftId: string | null;
    staffingType: 'Internal' | 'External' | null;
  }>({
    providerId: null,
    shiftId: null,
    staffingType: null,
  });

  const {
    initiated: isCalendarInitiallyLoaded,
    loading: calendarDataLoading,
    resourcesInitiated,
    resourcesList,
    loadCalendarData,
    entitiesGroups: projectData,
    unassignedShifts,
    assignmentsInitiated,
  } = useCalendarShiftsStore();

  const loading = !(isCalendarInitiallyLoaded && resourcesInitiated && assignmentsInitiated);
  const dataLoading = calendarDataLoading || Object.values(loadingById).some((shift) => shift);
  useEffect(() => {
    dayjs().locale({
      ...dayjs.Ls.en,
      weekStart: WeekDayStartMap[currentLocation?.workWeekDayStart || 'Sunday'],
    });

    const from = dayjs().week(dateRange.weekNumber).startOf('week');
    const to = from.add(6, 'days');

    setDateRange({ from, to, weekNumber: from.week() });
    setFilter({
      key: 'startDate',
      value: {
        from: from,
        to: to,
        timezone: currentLocation?.timezone || TIMEZONE_FALLBACK,
      },
    });
  }, [currentLocation]);

  useEffect(() => {
    if (!hasFilter('startDate')) {
      setFilter({
        key: 'startDate',
        value: {
          from: dateRange.from,
          to: dateRange.to,
          timezone: currentLocation?.timezone || TIMEZONE_FALLBACK,
        },
      });
    }
    if (hasFilter('startDate') && currentLocation) {
      const {
        value: { from, to },
      } = (getFilter('startDate') as DateRangeFilter) || {
        value: { from: null, to: null },
      };
      setDateRange({
        from: dayjs(from || dateRange.from),
        to: dayjs(to || dateRange.to),
        weekNumber: dayjs(from || dateRange.from).week(),
      });
      loadCalendarData(
        dayjs(from || dateRange.from).toDate(),
        dayjs(to || dateRange.to).toDate(),
        currentLocation.timezone,
        {
          ...activeFilters,
          locations: { key: 'locations', value: [currentLocationID] },
        },
        sortingModel,
      );
    }
  }, [currentLocationID, activeFilters, refreshTrigger, sortingModel]);

  const handleSortingChange = (field: string, type: 'asc' | 'desc' | null) => {
    if (type) {
      setSortingModel([{ field, sort: type }]);
    }
  };

  const handleTodayClick = () => {
    if (!dataLoading) {
      const dateStart = dayjs().startOf('week');
      setFilter({
        key: 'startDate',
        value: {
          from: dateStart,
          to: dateStart.add(6, 'days'),
          timezone: currentLocation?.timezone || TIMEZONE_FALLBACK,
        },
      });
    }
  };

  const handleWeekBackClick = () => {
    if (!dataLoading) {
      setFilter({
        key: 'startDate',
        value: {
          from: dateRange.from.subtract(7, 'day'),
          to: dateRange.from,
          timezone: currentLocation?.timezone || TIMEZONE_FALLBACK,
        },
      });
    }
  };
  const handleWeekForwardClick = () => {
    if (!dataLoading) {
      const {
        value: { from, to },
      } = (getFilter('startDate') as DateRangeFilter) || {
        value: { from: null, to: null },
      };
      const dateStart = dayjs(from || undefined).add(7, 'day');
      setFilter({
        key: 'startDate',
        value: {
          from: dateStart,
          to: dateStart.add(6, 'days'),
          timezone: currentLocation?.timezone || TIMEZONE_FALLBACK,
        },
      });
    }
  };

  const handleDayRangeChange = (start: Dayjs, end: Dayjs) => {
    if (!dataLoading) {
      setFilter({
        key: 'startDate',
        value: {
          from: start,
          to: end,
          timezone: currentLocation?.timezone || TIMEZONE_FALLBACK,
        },
      });
    }
  };

  const handlePublishShift = () => {
    openShiftPublishDrawer();
  };

  const handleShareShift = () => {
    openShiftShareDrawer();
  };

  const handleNewShift = () => {
    openShiftDrawer();
  };

  const handleCreateRotation = () => {
    openShiftRotationDrawer();
  };

  const handleCreateSeries = () => {
    openShiftSeriesDrawer();
  };

  const handleOpenSeriesDetailsDrawer = (shift: Shift) => {
    openSeriesDrawer(shift.recurrenceId);
  };

  const handleOpenRotationDetailsDrawer = (shift: Shift) => {
    openRotationDrawer(shift.recurrenceId);
  };

  const handleAssignClick = (provider: StaffListItem, assignments: Shift[], day: Dayjs) => {
    openAssignShiftDrawer(provider, assignments, day);
  };

  const [sendingData, setSendingData] = useState<{ shiftId: string; resourceId: string } | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [doubleShiftId, setDoubleShiftId] = useState<string | null>(null);
  const handleAssign = async (resourceInfo: StaffListItem, selectedShift: Shift) => {
    const resourceType = resourceInfo.staffingType === 'Agency' ? 'External' : 'Internal';
    if (selectedShift.recurrence === 'single' && resourceInfo.staffingType === 'Internal') {
      const overlapInfo = await checkAssignOverlap({
        shiftID: selectedShift.id as string,
        assigneeId: resourceInfo.id,
      });
      if (overlapInfo.canCreateDoubleShift) {
        setDoubleShiftId(overlapInfo.overlappedShiftId);
        setOpenConfirmDialog(true);
        setSendingData({ shiftId: selectedShift.id as string, resourceId: resourceInfo.id });
        return;
      }
    }
    if (selectedShift.recurrence !== 'single' && selectedShift.canHasMultipleAssignee === false) {
      setConfirmAssignDetails({
        providerId: resourceInfo.id,
        shiftId: selectedShift.recurrenceId,
        staffingType: resourceType,
      });
      setConfirmAssignOfEntireRecurrenceDialogOpen(true);
      return;
    }

    await performAssign(selectedShift.id as string, resourceInfo.id, null, resourceType);
  };

  const performAssign = async (
    shiftId: string,
    resourceId: string,
    doubleShiftId?: string | null,
    resourceType: 'Internal' | 'External' = 'Internal',
  ) => {
    if (!shiftId || !resourceId) return;
    try {
      if (resourceType === 'Internal') {
        await assignShift(shiftId, resourceId, doubleShiftId);
      }
      if (resourceType === 'External') {
        await requestAssignShiftOnExternal(shiftId, resourceId);
      }
    } catch (err) {
      const error = parseError(err, 'shifts.assignError');
      showErrorIntl(error.message);
    }
    setDoubleShiftId(null);
    setOpenConfirmDialog(false);
    callRefresh();
  };

  const handleConfirmAssignOfEntireRecurrence = async () => {
    // Logic for confirming the assignment of the entire recurrence
    try {
      confirmAssignDetails.shiftId &&
        confirmAssignDetails.providerId &&
        confirmAssignDetails.staffingType &&
        (await performAssign(
          confirmAssignDetails.shiftId,
          confirmAssignDetails.providerId,
          null,
          confirmAssignDetails.staffingType,
        ));
    } catch (err) {
      const error = parseError(err, 'shifts.assignError');
      showErrorIntl(error.message);
    }
    setConfirmAssignOfEntireRecurrenceDialogOpen(false);
  };

  const handleShiftEdit = (shift: Shift) => {
    if (shift && EDIT_ALLOWED_STATUSES.includes(shift.status.toLowerCase())) {
      openShiftDrawer(shift.id);
    }
  };

  const handleClearFilters = () => {};
  const [opedCancelDialog, setOpenCancelDialog] = useState(false);
  const [currentShift, setCurrentShift] = useState<Shift | null>(null);
  const handleCancel = (reason: string, reasonComment: string) => {
    if (currentShift && currentShift.id) {
      cancelShift(currentShift.id as string, reason === 'custom' ? reasonComment : reason)
        .then(() => {
          callRefresh();
          setOpenCancelDialog(false);
          showSuccessIntl('shifts.unassignSuccessful');

          Analytics.track('Shift: Canceled', {
            shift_id: currentShift.id,
            provider_qualification:
              currentShift.nurseQualifications.join(', ') + currentShift.otherQualifications.join(', '),
          });
        })
        .catch((error: unknown) => showError(parseError(error).message));
    }
  };

  const handleDeleteShift = (shift: Shift) => {
    deleteShift(shift.id as string)
      .then(() => {
        callRefresh();
        showSuccessIntl('shifts.deleteSuccessful');
      })
      .catch((error: unknown) => showError(parseError(error).message));
  };

  const handleHardDeleteShift = (shift: Shift) => {
    openHardDeletePopup(shift.id as string, shift?.externalId || null);
  };

  const handleUnassignShift = (shift: Shift) => {
    openShiftUnassignDialog(shift.id as string);
  };

  const handleOpenResourceInfo = (provider: StaffListItem) => {
    openViewProviderInfoDrawer(provider.id, provider.staffingType.toLowerCase() as 'internal' | 'external');
  };

  return (
    <Stack direction={'column'} height={'100%'} spacing={2}>
      <CalendarFilterBar
        loading={loading}
        startDate={dateRange.from}
        endDate={dateRange.to}
        weekStartDay={WeekDayStartMap[currentLocation?.workWeekDayStart || 'Sunday']}
        onToday={handleTodayClick}
        onWeekBack={handleWeekBackClick}
        onWeekForward={handleWeekForwardClick}
        onNewShift={handleNewShift}
        onNewRotation={handleCreateRotation}
        onNewSeries={handleCreateSeries}
        onListRefresh={callRefresh}
        onPublish={handlePublishShift}
        onShare={handleShareShift}
        onClearFilters={handleClearFilters}
        onDateRangeChange={handleDayRangeChange}
      />
      {loading ? (
        <Stack direction={'column'} height={'100%'}>
          <Box flex={1} />
          <SkeletonProgress show={loading} />
          <Box flex={1} />
        </Stack>
      ) : (
        <Calendar
          startDate={dateRange.from}
          endDate={dateRange.to}
          groups={projectData}
          providers={resourcesList}
          dataLoading={dataLoading}
          unassigned={unassignedShifts}
          onAssignMenuOpen={handleAssignClick}
          onAssign={handleAssign}
          onShiftEdit={handleShiftEdit}
          onShiftDeleteCall={handleDeleteShift}
          onShiftHardDeleteCall={handleHardDeleteShift}
          onShiftUnassignCall={handleUnassignShift}
          onShiftOpenRotation={handleOpenRotationDetailsDrawer}
          onShiftOpenSeries={handleOpenSeriesDetailsDrawer}
          onShiftCancelCall={(shift: Shift) => {
            setCurrentShift(shift);
            setOpenCancelDialog(true);
          }}
          onResourceClick={handleOpenResourceInfo}
          onSortingChange={handleSortingChange}
        />
      )}
      {opedCancelDialog && (
        <CancelDialog
          open={opedCancelDialog}
          onClose={() => setOpenCancelDialog(false)}
          onSubmit={handleCancel}
          loading={false}
        />
      )}
      <Drawers />

      <ConfirmAssignOfEntireRecurrenceDialog
        open={confirmAssignOfEntireRecurrenceDialog}
        onClose={() => setConfirmAssignOfEntireRecurrenceDialogOpen(false)}
        onSubmit={handleConfirmAssignOfEntireRecurrence}
      />
      <ConfirmCreateDoubleShiftDialog
        open={openConfirmDialog}
        onClose={() => {
          setDoubleShiftId(null);
          setOpenConfirmDialog(false);
        }}
        onSubmit={async () => {
          if (!sendingData) return;
          await performAssign(sendingData.shiftId as string, sendingData.resourceId as string, doubleShiftId, 'Internal');
        }}
      />
    </Stack>
  );
}

export function Drawers() {
  const { callRefresh } = useRefreshSyncStore();
  const { showSuccessIntl, showError } = useNotification();
  const { open: openAssignShiftDrawer } = useAssignShiftDrawerStore();
  const { open: hardDeletePopupOpen } = useHardDeleteModalStore();
  const { drawerOpen: seriesDrawerOpen } = useShiftSeriesAddEditStore();
  const { drawerOpen: rotationDrawerOpen } = useShiftRotationAddEditStore();
  const { drawerOpen: shiftEditDrawerOpen } = useShiftAddEditStore();
  const { drawerOpen: shareDrawerOpen } = useShareDrawerStore();
  const { drawerOpen: publishDrawerOpen } = usePublishDrawerStore();
  const { drawerOpen: seriesDetailsDrawerOpen } = useShiftSeriesEditStore();
  const { drawerOpen: rotationDetailsDrawerOpen } = useRotationSeriesEditStore();
  const { open: oenTalentViewDrawer } = useViewProviderInfoStore();

  const { unassignDialogOpen, cancelShift } = useShiftsAssignment();
  const { loading: locationsLoading, currentLocationLoading } = useLocationStore();
  const { initiated: isCalendarInitiallyLoaded, resourcesInitiated, assignmentsInitiated } = useCalendarShiftsStore();

  const { initiated: qualListInitiated } = useOrgQualificationsStore();

  let loading =
    !(isCalendarInitiallyLoaded && resourcesInitiated && assignmentsInitiated && qualListInitiated) ||
    locationsLoading ||
    currentLocationLoading;

  return (
    <>
      {seriesDetailsDrawerOpen && (
        <SeriesDetailsDrawer
          onSaved={() => {
            callRefresh();
          }}
        />
      )}
      {rotationDetailsDrawerOpen && (
        <RotationDetailsDrawer
          onSaved={() => {
            callRefresh();
          }}
        />
      )}
      {openAssignShiftDrawer && (
        <AssignShifts
          onSaveCall={() => {
            callRefresh();
          }}
        />
      )}
      {hardDeletePopupOpen && (
        <ShiftHardDeleteDialog
          afterSubmit={() => {
            callRefresh();
          }}
        />
      )}
      {shiftEditDrawerOpen && (
        <EditShiftDrawer
          onSaveCall={() => {
            callRefresh();
          }}
        />
      )}
      {seriesDrawerOpen && (
        <EditSeriesDrawer
          onSaveCall={() => {
            callRefresh();
          }}
        />
      )}
      {rotationDrawerOpen && (
        <EditRotationDrawer
          onSaveCall={() => {
            callRefresh();
          }}
        />
      )}
      {unassignDialogOpen && (
        <CalendarUnassignDialog
          onAction={() => {
            callRefresh();
          }}
        />
      )}
      {shareDrawerOpen && (
        <ShareShifts
          onSaveCall={() => {
            callRefresh();
          }}
        />
      )}
      {publishDrawerOpen && (
        <PublishShiftsDrawer
          onSaveCall={() => {
            callRefresh();
          }}
        />
      )}
      {oenTalentViewDrawer && <TalentViewDrawer />}
    </>
  );
}
